"use client";

import { useIsFetching, useIsMutating } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

/**
 * The one line the app draws to say "something is in flight".
 *
 * Every surface here fetches on the client, so until the data lands a page
 * is a frame full of skeletons and a reader has no way to tell a slow
 * request from a finished one that simply has little to show. This is that
 * signal, and it is deliberately the ONLY global one: per-card spinners
 * stay where they are, because they say WHICH card is waiting, which a bar
 * at the top of the window cannot.
 *
 * WHAT IT TRACKS. React Query's in-flight count, queries and mutations
 * together. That is the honest source: it is what the pages actually wait
 * on. Route transitions are not counted — in a production build they are
 * imperceptible next to the fetch, and animating a bar for them would mean
 * inventing progress the app does not have.
 *
 * WHY IT DOES NOT APPEAR IMMEDIATELY. Most endpoints on this stack now
 * answer in twenty to fifty milliseconds. A bar that flashed for every one
 * of those would strobe on every click, and strobing is worse than silence
 * because the reader learns to ignore it. So nothing is drawn for the
 * first `SHOW_DELAY_MS`, and anything faster than that stays invisible —
 * the bar is a signal that something is SLOW, not that something happened.
 * Once it does appear it holds for `MIN_VISIBLE_MS`, so a request that
 * resolves just past the delay does not blink in and out.
 *
 * WHY IT NEVER REACHES THE END ON ITS OWN. The width creeps toward
 * `CREEP_CEILING` and stops. We do not know how far along a fetch is, and
 * a bar that ran to 100% and sat there would be a lie the reader catches
 * the second time they see it. It completes only when the work does.
 */

/** Nothing is drawn before this. Faster than this and the bar never shows. */
const SHOW_DELAY_MS = 180;
/** Once visible, hold at least this long so it cannot blink. */
const MIN_VISIBLE_MS = 320;
/** How often the creep advances while work is outstanding. */
const CREEP_INTERVAL_MS = 240;
/** The creep approaches this and never passes it. Only completion does. */
const CREEP_CEILING = 92;
/** Share of the remaining distance covered per step — decelerating, so a
 *  long wait keeps moving without ever promising an end. */
const CREEP_EASING = 0.18;
/** Width the bar starts at, so its first frame is visible rather than a
 *  hairline that has to grow into view. */
const CREEP_START = 8;
/** Fade after the bar completes, before it unmounts. */
const FADE_MS = 260;

export function GlobalLoadingBar() {
  const busy = useIsFetching() + useIsMutating() > 0;

  const [visible, setVisible] = useState(false);
  const [width, setWidth] = useState(0);

  // Read inside the busy effect without making it a dependency: adding
  // `visible` there would tear the effect down the moment it sets it, and
  // the creep would never survive its first tick.
  const visibleRef = useRef(false);
  const shownAt = useRef(0);

  useEffect(() => {
    visibleRef.current = visible;
  }, [visible]);

  // Show on a delay; on completion, hold the floor, fill, then leave.
  useEffect(() => {
    if (busy) {
      const show = setTimeout(() => {
        shownAt.current = Date.now();
        setWidth(CREEP_START);
        setVisible(true);
      }, SHOW_DELAY_MS);
      return () => clearTimeout(show);
    }

    if (!visibleRef.current) return;

    let fade: ReturnType<typeof setTimeout> | undefined;
    const held = Date.now() - shownAt.current;
    const complete = setTimeout(
      () => {
        setWidth(100);
        fade = setTimeout(() => {
          setVisible(false);
          setWidth(0);
        }, FADE_MS);
      },
      Math.max(0, MIN_VISIBLE_MS - held),
    );

    return () => {
      clearTimeout(complete);
      if (fade) clearTimeout(fade);
    };
  }, [busy]);

  // Creep only while there is still work. Without the `busy` guard this
  // would keep advancing during the fill and drag the bar back off 100%.
  useEffect(() => {
    if (!visible || !busy) return;
    const id = setInterval(() => {
      setWidth((w) =>
        w >= CREEP_CEILING ? w : w + (CREEP_CEILING - w) * CREEP_EASING,
      );
    }, CREEP_INTERVAL_MS);
    return () => clearInterval(id);
  }, [visible, busy]);

  if (!visible) return null;

  const done = width >= 100;

  return (
    // aria-hidden on purpose. The bar carries no information a screen
    // reader can act on, and the surfaces underneath already announce
    // their own waiting state through <Spinner label>. Announcing this
    // too would narrate every fetch on the platform.
    <div
      aria-hidden
      // ONE pixel. This started at three, which turned a background signal
      // into a foreground one: at that weight it reads as a progress bar the
      // reader is meant to watch, and it competes with the page for
      // attention every time anything loads. It is not that kind of
      // indicator. It should be caught out of the corner of the eye and
      // otherwise ignored, so it is a hairline at the very top edge.
      className="pointer-events-none fixed inset-x-0 top-0 z-[100] h-px"
    >
      <div
        // No glow and no rounded cap either. Both were carrying the old
        // weight: a 10px bloom around a one-pixel line is thicker than the
        // line, which puts back exactly the mass this change removes, and a
        // rounded end on a single pixel renders as a faded tip that just
        // makes the leading edge look unfinished.
        className={`mi-loading-bar h-full bg-accent transition-[width,opacity] ease-out ${
          done ? "opacity-0 duration-200" : "opacity-100 duration-300"
        }`}
        style={{ width: `${width}%` }}
      />
    </div>
  );
}
